import React, { useEffect, useMemo, useState } from "react"
import { formatDate } from "../utils/formatDate.js"
import { parseDate } from "../utils/parseDate.js"
import { formatTimeAgo } from "../utils/timeAgo.js"

const DISPLAY_MODE_STORAGE_KEY = "sidebar_build_number_display_mode"
const LAST_UPDATED_PREFIX = "Last updated: "

function withLastUpdatedPrefix(value) {
    return value ? `${LAST_UPDATED_PREFIX}${value}` : ""
}

/** @returns {"absolute" | "relative"} */
function readStoredDisplayMode() {
    try {
        const saved = localStorage.getItem(DISPLAY_MODE_STORAGE_KEY)
        return saved === "relative" ? "relative" : "absolute"
    } catch {
        return "absolute"
    }
}

/**
 * @param {{
 *   buildNumber?: string,
 *   loading?: boolean,
 *   isCollapsed?: boolean,
 * }} props
 */
export default function SidebarLastUpdated({
    buildNumber = "",
    loading = false,
    isCollapsed = false,
}) {
    const [nowMs, setNowMs] = useState(() => Date.now())
    const [displayMode, setDisplayMode] = useState(readStoredDisplayMode)

    // @ts-ignore - Vite injects `import.meta.env` at build time
    const buildNumberTimeZone = import.meta.env.VITE_BUILD_NUMBER_TIMEZONE || "Canada/Pacific"

    useEffect(() => {
        const timer = window.setInterval(() => setNowMs(Date.now()), 30 * 1000)
        return () => window.clearInterval(timer)
    }, [])

    const buildDate = useMemo(() => parseDate.utc(buildNumber), [buildNumber])
    const nowDate = useMemo(() => new Date(nowMs), [nowMs])

    const absoluteLabel = useMemo(
        () => withLastUpdatedPrefix(
            buildDate ? formatDate.readableTimestampWithTimeZone(buildDate, buildNumberTimeZone) : ""
        ),
        [buildDate, buildNumberTimeZone]
    )
    const relativeLabel = useMemo(
        () => withLastUpdatedPrefix(buildDate ? formatTimeAgo(buildDate, nowDate) : ""),
        [buildDate, nowDate]
    )

    const displayLabel = displayMode === "absolute" ? absoluteLabel : relativeLabel
    const tooltipLabel = displayMode === "absolute" ? relativeLabel : absoluteLabel

    function toggleDisplayMode() {
        setDisplayMode((current) => {
            const next = current === "absolute" ? "relative" : "absolute"
            try {
                localStorage.setItem(DISPLAY_MODE_STORAGE_KEY, next)
            } catch {
                // Ignore storage failures; toggle still works for this session.
            }
            return next
        })
    }

    if (isCollapsed) {
        return null
    }

    if (loading) {
        return (
            <div
                className="d-flex justify-content-center w-100"
                style={{ marginTop: "0.5rem", padding: "0 0.25rem" }}
            >
                <div
                    className="spinner-border spinner-border-sm text-white-50"
                    role="status"
                    style={{ width: "0.75rem", height: "0.75rem", borderWidth: "0.12em", opacity: 0.6 }}
                >
                    <span className="visually-hidden">Loading build number</span>
                </div>
            </div>
        )
    }

    if (!displayLabel) {
        return null
    }

    return (
        <button
            type="button"
            className="sidebar-last-updated-btn nav-text text-white-50 text-center w-100 border-0"
            style={{
                fontSize: "0.75rem",
                marginTop: "0.5rem",
                padding: "0.375rem 0.625rem",
                borderRadius: "0.5rem",
                lineHeight: 1.35,
            }}
            title={tooltipLabel || buildNumber}
            aria-label={tooltipLabel ? `${displayLabel}. ${tooltipLabel}` : displayLabel}
            onClick={toggleDisplayMode}
        >
            {displayLabel}
        </button>
    )
}
